import type { Ingredient } from './types';
import type { IngredientInput } from './validation';
import { matchIngredient, type IngredientMatch } from './recipeProposal';
import { parseImporte, type ScannedLine } from './receipts';
import { parseUnit } from './importCatalog';
import { isPurchaseUnit, dimensionOf, toBase, type PurchaseUnit } from './units';
import { verificarLinea, fueraDelFreno, type DudaPrecio } from './escanerLineas';
import { formatoDeLinea, matchesNoIngredientRule, noEsIngrediente, type InvoiceLineFormat, type NoIngredientReason, type NoIngredientRule } from './ingredientAliases';

export interface CatalogPriceLine {
  index: number;
  description: string;
  name: string;
  match: IngredientMatch;
  action: 'update' | 'create' | 'choose' | 'ignore';
  unitPrice?: number;
  unit?: PurchaseUnit;
  warning?: string;
  comparison?: { previous: number; current: number; changePct: number; unit: PurchaseUnit };
  /**
   * Cantidad comprada y formato, si la factura los dice (fase 7): «6
   * botellas de 1 L» = count 6, pack 1 L, envase botella. Con formato y sin
   * unidad en la cantidad, `unitPrice` es el precio de UNA botella.
   */
  bought?: InvoiceLineFormat;
  /**
   * Por qué no es un ingrediente: lo dicen las reglas (limpieza, gas…), lo
   * recordó Alexia porque el usuario lo marcó con ese proveedor, o es un abono.
   */
  ignoreReason?: NoIngredientReason | 'recordado' | 'abono' | 'sin-nombre';
  /*
   * Bloque S (escáner único, 14 oct 2026): «PRECIO UNITARIO, NUNCA EL TOTAL
   * DE LA LÍNEA». Lo leído de cada línea y si cuadra: cantidad × precio
   * unitario = importe (± 0,05). `unitPrice` ya es el comprobado (o importe ÷
   * cantidad); con `duda` la línea va a revisar y su precio nunca se aplica solo.
   */
  /** Cuántos se compraron (la cantidad de la línea). */
  qty?: number;
  /** Importe de la línea tal cual lo dice la factura. */
  lineTotal?: number;
  duda?: DudaPrecio;
  /** Viene en la misma presentación que la última vez (sin ingrediente: ausente). */
  presentacionIgual?: boolean;
}
/** Resumen de la factura de la que salen los precios (cuando el gasto ya se guardó). */
export interface FacturaDePrecios {
  proveedor: string;
  supplierId?: string;
  total: number;
  fecha: string;
  expenseId?: string;
  esNota?: boolean;
}
export interface CatalogPricesProposal {
  lines: CatalogPriceLine[];
  /** Bloque S: la factura de estos precios, para «Por revisar» cuando su gasto ya se guardó. */
  factura?: FacturaDePrecios;
}

export interface CatalogProposalOptions {
  /** Proveedor de la factura: decide qué alias y qué «no es un ingrediente» aplican. */
  supplierId?: string;
  /** Líneas que el usuario ya marcó como «No es un ingrediente». */
  noIngredientRules?: Array<Pick<NoIngredientRule, 'text' | 'supplierId'>>;
}

/** El precio leído es por unidad facturada, no por el formato previo del catálogo. */
export function invoiceLinesToCatalogProposal(lines: ScannedLine[], catalog: Ingredient[], options: CatalogProposalOptions = {}): CatalogPricesProposal {
  return { lines: lines.map((line, index) => {
    const description = line.description?.trim() ?? '';
    const name = description.replace(/\b\d+(?:[.,]\d+)?\s*(kg|g|gr|grs|ml|cl|l|lt|lts|litros?|gramos?|kilos?)\b/gi, '')
      // Bloque S: «PAQ 6U», «C/30U» son la presentación, no el nombre.
      .replace(/\b(?:paq|paqs|pqt|pq|paquetes?)\.?\s?(?:de\s)?\d{1,3}\s?(?:u|un|uds?|pz|pzs|pzas?|piezas?)?(?=\s|$)/gi, '')
      .replace(/\bc\/\s?\d{1,3}\s?(?:u|uds?|pz|pzs)\b/gi, '')
      .replace(/\s+/g, ' ').trim();
    // «ACEITE DE AGUACATE BOTELLA»: el envase no es parte del nombre.
    const sinEnvase = name.replace(/\b(botellas?|bolsas?|frascos?|botes?|cajas?|sacos?|latas?|garrafas?|garraf[oó]n(es)?|paquetes?|bultos?|cubetas?)\b/gi, '').replace(/\s+/g, ' ').trim();
    const directo = matchIngredient(name, catalog, { supplierId: options.supplierId });
    const match = directo.kind !== 'exact' && sinEnvase && sinEnvase !== name
      ? (() => { const otro = matchIngredient(sinEnvase, catalog, { supplierId: options.supplierId }); return otro.kind === 'exact' ? otro : directo; })()
      : directo;
    // La unidad debe corresponder a la cantidad facturada: «botella 750 ml»
    // en la descripción no significa que el precio sea por mililitro.
    const unitToken = `${line.quantity ?? ''}`.match(/\b(kg|g|gr|ml|cl|l|litros?|gramos?|kilos?|uds?|pz|docena|lb|gal|fl\.?\s?oz|oz)\b/i)?.[1];
    const parsedUnit = unitToken ? parseUnit(unitToken) : undefined;
    const formato = formatoDeLinea(description, line.quantity);
    // «26 UD» de «PAN BRIOCHE PAQ 8U»: lo que se cuenta son paquetes, no
    // piezas sueltas (revisión del 14 oct 2026: no sacaba la presentación).
    const unidadesSonPaquetes = parsedUnit === 'pz' && formato.envase === 'paquete' && (formato.packQty ?? 0) > 1;
    const unit = isPurchaseUnit(parsedUnit) && !unidadesSonPaquetes ? parsedUnit : undefined;
    // Bloque S: el precio de UNA unidad, comprobado contra el importe de la línea.
    const leida = verificarLinea(line);
    const unitPrice = leida.unitPrice != null && Number.isFinite(leida.unitPrice) && leida.unitPrice > 0 ? leida.unitPrice : undefined;
    // Con unidad en la cantidad («10 kg») el precio es por esa unidad: el
    // tamaño de la descripción no es el formato del precio.
    const bought: InvoiceLineFormat | undefined = unit
      ? (formato.count ? { count: formato.count } : undefined)
      : (formato.count || formato.packQty || formato.envase ? formato : undefined);
    // Un abono (cantidad o importe en negativo: envases retornables) no es un
    // precio de compra.
    const abono = (parseImporte(line.quantity) ?? 0) < 0 || (parseImporte(line.total) ?? 0) < 0;
    const recordado = matchesNoIngredientRule(name, options.noIngredientRules ?? [], options.supplierId);
    // Lo que ya está en tu catálogo con ese nombre (o con un alias tuyo) manda
    // sobre las palabras clave: quien lleva las bolsas como ingrediente, las lleva.
    const enCatalogo = match.kind === 'exact';
    const motivo = enCatalogo ? undefined : noEsIngrediente(description);
    const ignoreReason: CatalogPriceLine['ignoreReason'] = !name ? 'sin-nombre' : abono ? 'abono'
      : recordado && !match.candidates[0]?.alias ? 'recordado' : motivo;
    const pack = !unit && bought?.packQty && bought.packUnit ? { qty: bought.packQty, unit: bought.packUnit } : undefined;
    const current = match.kind === 'exact' ? catalog.find((ing) => ing.ingredientId === match.candidates[0]?.ingredientId) : undefined;
    const comparison = current && unit ? compareCatalogPrice(current, unitPrice, 1, unit)
      : current && pack ? compareCatalogPrice(current, unitPrice, pack.qty, pack.unit) : undefined;
    // Freno extra: más del triple o menos de un tercio = casi seguro leído mal.
    const duda: DudaPrecio | undefined = leida.duda ?? (comparison && fueraDelFreno(comparison.previous, comparison.current) ? 'x3' : undefined);
    const presentacionIgual = !current || !current.purchaseUnit || !(current.purchaseQty! > 0) ? undefined
      : unit ? current.purchaseUnit === unit && current.purchaseQty === 1
      : pack ? dimensionOf(current.purchaseUnit) === dimensionOf(pack.unit) && Math.abs(toBase(current.purchaseQty!, current.purchaseUnit) - toBase(pack.qty, pack.unit)) < 1e-6
      : false;
    return { index, description, name, match,
      action: ignoreReason ? 'ignore' : match.kind === 'exact' ? 'update' : 'choose',
      unitPrice, unit,
      comparison,
      warning: !unit && !pack ? 'Indica la unidad y cantidad a las que corresponde el precio de esta factura.' : !unitPrice ? 'Falta un precio unitario válido sin IVA.' : undefined,
      ...(bought ? { bought } : {}),
      ...(ignoreReason ? { ignoreReason } : {}),
      ...(leida.qty != null ? { qty: leida.qty } : {}),
      ...(leida.lineTotal != null ? { lineTotal: leida.lineTotal } : {}),
      ...(duda ? { duda } : {}),
      ...(presentacionIgual != null ? { presentacionIgual } : {}),
    };
  }) };
}

/** Comparación de compra sin merma ni IVA, sobre una misma unidad. */
export function compareCatalogPrice(current: Ingredient | undefined, price: number | undefined, qty: number, unit: PurchaseUnit) {
  if (!current || current.pricePending || !current.purchaseUnit || !(current.purchaseQty! > 0) || !(current.netPrice! > 0) || !(price! > 0) || !Number.isFinite(price) || !(qty > 0) || !Number.isFinite(qty) || dimensionOf(current.purchaseUnit) !== dimensionOf(unit)) return undefined;
  const previous = current.netPrice! / toBase(current.purchaseQty!, current.purchaseUnit) * toBase(1, unit);
  const next = price! / qty;
  return { previous, current: next, changePct: (next / previous - 1) * 100, unit };
}

/** Se llama solo tras revisar precio y formato, conservando el resto de la ficha. */
export function catalogPriceInput(current: Ingredient | undefined, name: string, price: number, qty: number, unit: PurchaseUnit): IngredientInput {
  return { ...current, name, group: current?.group ?? 'otros', vatPct: current?.vatPct ?? 0,
    purchaseQty: qty, purchaseUnit: unit, netPrice: price, pricePending: false,
    priceSource: 'factura', createdVia: current?.createdVia ?? 'factura', active: current?.active ?? true,
  };
}

/** Identity of an invoice product: a different supplier or pack is a separate ingredient. */
export function invoiceIngredientKey(input: Pick<Ingredient, 'name' | 'supplierId' | 'presentation' | 'purchaseQty' | 'purchaseUnit'>): string {
  const text = (value?: string) => (value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase().replace(/\s+/g, ' ');
  return JSON.stringify([text(input.name), input.supplierId ?? '', text(input.presentation), input.purchaseQty, input.purchaseUnit]);
}
